import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { api } from '../lib/api';
import { DateRangeFilter, type DateRangeChange } from './filters/DateRangeFilter';

const TX_TYPES: { value: string; label: string }[] = [
  { value: '', label: 'Loại giao dịch' },
  { value: 'purchase', label: 'Purchase' },
  { value: 'refund', label: 'Refund' },
  { value: 'fee', label: 'Fee' },
  { value: 'decline', label: 'Decline' },
];

const FILTER_PARAMS = [
  'date_from',
  'date_to',
  'card',
  'department',
  'type',
  'q',
  'has_alert',
  'amount_min',
  'amount_max',
];

function countActiveFilters(params: URLSearchParams): number {
  return FILTER_PARAMS.filter((k) => {
    const v = params.get(k);
    return v !== null && v !== '';
  }).length;
}

// ── component ─────────────────────────────────────────────────────────────────

type FilterField = 'date' | 'card' | 'department' | 'type' | 'search' | 'has_alert' | 'amount';

export function TransactionsFilters({
  omit = [],
}: {
  /** Hide these filter fields. Defaults to showing everything. */
  omit?: FilterField[];
} = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const has = (f: FilterField) => !omit.includes(f);

  const activeCount = countActiveFilters(searchParams);

  // Debounced search input
  const [searchDraft, setSearchDraft] = useState(searchParams.get('q') ?? '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep searchDraft in sync if URL changes externally (e.g. browser back)
  useEffect(() => {
    const q = searchParams.get('q') ?? '';
    setSearchDraft(q);
  }, [searchParams]);

  const { data: cardsData } = useQuery({
    queryKey: ['cards-lookup'],
    queryFn: () => api.listCardsLookup(),
    staleTime: 5 * 60_000,
  });

  const { data: deptsData } = useQuery({
    queryKey: ['departments-lookup'],
    queryFn: () => api.listDepartments(),
    staleTime: 5 * 60_000,
  });

  // ── handlers ───────────────────────────────────────────────────────────────

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
      next.set('page', '1');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  function handleDateChange(next: DateRangeChange) {
    const p = new URLSearchParams(searchParams);
    // next.all is always false here (defaultPick="all" → empty URL already means
    // all-time, so DateRangeFilter never emits all:true). The range handling is
    // kept symmetric with DashboardFilters; the transactions API ignores `range`.
    if (next.all) p.set('range', 'all');
    else p.delete('range');
    if (next.from) p.set('date_from', next.from);
    else p.delete('date_from');
    if (next.to) p.set('date_to', next.to);
    else p.delete('date_to');
    p.set('page', '1');
    setSearchParams(p);
  }

  function clearAll() {
    const next = new URLSearchParams();
    next.set('page', '1');
    setSearchParams(next);
    setSearchDraft('');
  }

  function handleSearchChange(value: string) {
    setSearchDraft(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setParam('q', value.trim() || null);
    }, 300);
  }

  const hasAlertActive = searchParams.get('has_alert') === '1';

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-2 rounded-lg border border-border bg-card p-3">
      {/* Row 1 — Quick-pick date buttons + custom date range on same line */}
      {has('date') && <DateRangeFilter defaultPick="all" onChange={handleDateChange} />}

      {/* Row 2 — Other filters */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        {/* Card dropdown */}
        {has('card') && cardsData && cardsData.rows.length > 0 && (
          <select
            value={searchParams.get('card') ?? ''}
            onChange={(e) => setParam('card', e.target.value || null)}
            className="rounded border border-border bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">Tất cả thẻ</option>
            {cardsData.rows.map((c) => (
              <option key={c.id} value={c.id}>
                {c.alias} ({c.bank_code} …{c.last4})
              </option>
            ))}
          </select>
        )}

        {/* Department dropdown */}
        {has('department') && deptsData && deptsData.rows.length > 0 && (
          <select
            value={searchParams.get('department') ?? ''}
            onChange={(e) => setParam('department', e.target.value || null)}
            className="rounded border border-border bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          >
            <option value="">Tất cả bộ phận</option>
            <option value="unassigned">Chưa gán</option>
            {deptsData.rows.map((d) => (
              <option key={d.id} value={d.id}>
                {d.code} — {d.name}
              </option>
            ))}
          </select>
        )}

        {/* Type dropdown */}
        {has('type') && (
          <select
            value={searchParams.get('type') ?? ''}
            onChange={(e) => setParam('type', e.target.value || null)}
            className="rounded border border-border bg-background px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {TX_TYPES.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        )}

        {/* Merchant search */}
        {has('search') && (
          <input
            type="search"
            placeholder="Tìm merchant…"
            value={searchDraft}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="min-w-[160px] rounded border border-border bg-background px-2 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        )}

        {/* Has alert toggle */}
        {has('has_alert') && (
          <Button
            variant={hasAlertActive ? 'default' : 'secondary'}
            onClick={() => setParam('has_alert', hasAlertActive ? null : '1')}
            aria-pressed={hasAlertActive}
            className="inline-flex items-center gap-1.5"
          >
            <AlertTriangle size={12} strokeWidth={1.5} />
            Has Alert
          </Button>
        )}

        {/* Amount range (VND) — datalist gives preset options but user can type any value */}
        {has('amount') && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <datalist id="amount-presets">
              <option value="0">0 ₫</option>
              <option value="100000">100.000 ₫</option>
              <option value="500000">500.000 ₫</option>
              <option value="1000000">1.000.000 ₫</option>
              <option value="5000000">5.000.000 ₫</option>
            </datalist>
            <span>VND từ</span>
            <input
              type="number"
              min="0"
              step="1000"
              inputMode="numeric"
              list="amount-presets"
              placeholder="0"
              value={searchParams.get('amount_min') ?? ''}
              onChange={(e) => setParam('amount_min', e.target.value || null)}
              className="w-32 rounded border border-border bg-background px-2 py-1 text-right text-sm tabular-nums text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <span>đến</span>
            <input
              type="number"
              min="0"
              step="1000"
              inputMode="numeric"
              list="amount-presets"
              placeholder="∞"
              value={searchParams.get('amount_max') ?? ''}
              onChange={(e) => setParam('amount_max', e.target.value || null)}
              className="w-32 rounded border border-border bg-background px-2 py-1 text-right text-sm tabular-nums text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        )}

        {/* Active count badge + clear all */}
        <div className="ml-auto flex items-center gap-2">
          {activeCount > 0 && (
            <Badge variant="info">
              {activeCount} filter{activeCount > 1 ? 's' : ''} applied
            </Badge>
          )}
          {activeCount > 0 && (
            <Button variant="ghost" onClick={clearAll}>
              Clear all
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
